import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { authService, userService } from '../../../services/supabaseService';
import { getPasswordChecks, validatePassword } from '../../../utils/passwordRules';

const UserModal = ({ 
  isOpen, 
  onClose, 
  user, 
  currentUserRole,
  currentUserId,
  onSave,
  mode // 'create' or 'edit'
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    role: 'inspector',
    avatar_url: '',
    is_active: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const loadUserProfiles = async (userId) => {
    try {
      const { data, error } = await userService.getUserProfile(userId); 
      if (error) {
        console.error('Erreur lors du chargement du profil utilisateur:', error);
        throw error;
      }
      return data;
    } catch (error) {
      console.error('Erreur finale lors du chargement du profil utilisateur:', error);
      return null;
    }
  };

  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({
        email: user?.email || '',
        password: '',
        confirmPassword: '',
        full_name: user?.user_profile?.full_name || user?.full_name || '',
        phone: user?.user_profile?.phone || user?.phone || '',
        role: user?.user_profile?.role || user?.role || 'technician',
        avatar_url: user?.user_profile?.avatar_url || user?.avatar_url || '',
        is_active: user?.user_profile?.is_active ?? user?.is_active ?? true
      });
    } else if (mode === 'create') {
      const initialData = {
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
        phone: '',
        role: 'technician',
        avatar_url: '',
        is_active: true
      };
      
      setFormData(initialData);
    }
    setErrors({});
    setShowPassword(false);
  }, [user, mode, isOpen]);

  const roleOptions = [
    { value: 'technicien', label: 'technician' },
    { value: 'admin', label: 'Admin' },
    { value: 'client', label: 'Client' },
    //{ value: 'dispatch', label: 'Dispatch' },
   // { value: 'trainer', label: 'Formateur' },
    { value: 'logistician', label: 'Logisticien' },
    { value: 'manager', label: 'Manager' }
  ];

  if (currentUserRole === 'super_admin') {
    roleOptions.unshift({ value: 'super_admin', label: 'Admin' });
  }

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData?.email) {
      newErrors.email = 'Veuillez saisir une adresse email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Adresse email invalide';
    }

    // Password validation (only for create mode)
    if (mode === 'create') {
      const passwordError = validatePassword(formData.password, formData.confirmPassword);
      if (passwordError) {
        if (passwordError.includes('correspondent')) {
          newErrors.confirmPassword = passwordError;
        } else {
          newErrors.password = passwordError;
        }
      }
      if (!formData?.confirmPassword) {
        newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
      }
    }

    // Full name validation
    if (!formData?.full_name?.trim()) {
      newErrors.full_name = 'Veuillez saisir le nom complet';
    } else if (formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Le nom doit contenir au moins 2 caractères';
    }

    // Phone validation
    if (!formData?.phone) {
      newErrors.phone = 'Veuillez saisir un numéro de téléphone';
    } else if (!/^[\d\s+()-]+$/.test(formData.phone)) {
      newErrors.phone = 'Numéro de téléphone invalide';
    }

    // Role validation
    if (!formData?.role) {
      newErrors.role = 'Veuillez sélectionner un rôle';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (mode === 'create') {
        const userDataToSend = {
          fullName: formData.full_name,
          role: formData.role,
          phone: formData.phone,
          avatar_url: formData.avatar_url
        };

        console.log('📤 Données envoyées au service:', userDataToSend);

        const { data, error } = await authService.CreateUser(
          formData.email,
          formData.password,
          userDataToSend
        );

        if (error) {
          throw new Error(error.message || 'Erreur lors de la création de l\'utilisateur');
        }

        console.log('✅ User created successfully:', data);
        
      } else {
        const userId = user?.id;
        if (!userId) {
          throw new Error('Utilisateur introuvable');
        }

        const profileUpdates = {
          full_name: formData.full_name,
          phone: formData.phone,
          role: formData.role,
          is_active: formData.is_active,
          avatar_url: formData.avatar_url || null,
        };

        const { error: profileError } = await userService.updateUserProfile(userId, profileUpdates);
        if (profileError) {
          throw new Error(profileError.message || 'Erreur lors de la mise à jour du profil');
        }

        onSave?.({
          ...user,
          ...profileUpdates,
          email: formData.email,
          id: userId,
        });
      }
      
      onClose();
      window.location.reload(); 
      
    } catch (err) {
      console.error('❌ Error saving user:', err);
      
      let errorMessage = 'Erreur lors de la sauvegarde';
      
      if (err.message.includes('User already registered')) {
        errorMessage = 'Un utilisateur avec cet email existe déjà';
      } else if (err.message.includes('Email not confirmed')) {
        errorMessage = 'Email non confirmé';
      } else if (err.message.includes('Invalid email')) {
        errorMessage = 'Adresse email invalide';
      } else if (err.message.includes('Erreur réseau')) {
        errorMessage = 'Erreur de connexion. Veuillez vérifier votre connexion internet.';
      } else {
        errorMessage = err.message;
      }
      
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-300 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[140vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {mode === 'create' ? 'Nouvel utilisateur' : 'Modifier l\'utilisateur'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === 'create' 
                ? 'Créer un nouveau compte utilisateur' 
                : 'Modifier les informations de l\'utilisateur'
              }
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {errors.submit && (
              <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-700">
                {errors.submit}
              </div>
            )}

            {/* Informations de connexion */}
            <div className="border-b border-border pb-6">
              <h3 className="text-lg font-medium text-foreground mb-4">
                Informations de connexion
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="email"
                  label="Adresse email"
                  required
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  placeholder="utilisateur@exemple.com"
                  disabled={mode === 'edit' || isSubmitting}
                />

                {mode === 'edit' && (
                  <div className="flex items-center space-x-2 pt-6">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      className="rounded border-border text-primary focus:ring-primary"
                      disabled={isSubmitting}
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-foreground">
                      Compte actif
                    </label>
                  </div>
                )}
              </div>

              {/* Champs mot de passe — création uniquement */}
              {mode === 'create' && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-foreground mb-3">Mot de passe</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        label="Mot de passe"
                        required
                        value={formData?.password}
                        onChange={(e) => handleInputChange('password', e?.target?.value)}
                        error={errors?.password}
                        placeholder="Saisir le mot de passe"
                        disabled={isSubmitting}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
                        disabled={isSubmitting}
                      >
                        <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
                      </button>
                    </div>

                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        label="Confirmer le mot de passe"
                        required
                        value={formData?.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                        error={errors?.confirmPassword}
                        placeholder="Confirmer le mot de passe"
                        disabled={isSubmitting}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                  <ul className="mt-3 space-y-1.5 rounded-lg border border-border bg-muted/30 p-3">
                    {getPasswordChecks(formData.password).map((check) => (
                      <li
                        key={check.id}
                        className={`flex items-center gap-2 text-xs ${
                          check.ok ? 'text-success' : 'text-muted-foreground'
                        }`}
                      >
                        <Icon name={check.ok ? 'CheckCircle' : 'Circle'} size={14} />
                        <span>{check.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Informations personnelles */}
            <div className="border-b border-border pb-6">
              <h3 className="text-lg font-medium text-foreground mb-4">
                Informations personnelles
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="text"
                  label="Nom complet"
                  required
                  value={formData?.full_name}
                  onChange={(e) => handleInputChange('full_name', e?.target?.value)}
                  error={errors?.full_name}
                  placeholder="Jean Dupont"
                  disabled={isSubmitting}
                />

                <Input
                  type="tel"
                  label="Téléphone"
                  required
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  error={errors?.phone}
                  placeholder="+33 6 12 34 56 78"
                  disabled={isSubmitting}
                />
              </div>

              <div className="mt-4">
                <Input
                  type="text"
                  label="URL Avatar (optionnel)"
                  value={formData?.avatar_url}
                  onChange={(e) => handleInputChange('avatar_url', e?.target?.value)}
                  placeholder="https://example.com/avatar.jpg"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Rôle et Tenant */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">
                Rôle et accès
              </h3>

              <div className="space-y-4">
                {/* Role Selection */}
                <Select
                  label="Rôle"
                  required
                  options={roleOptions}
                  value={formData?.role}
                  onChange={(value) => handleInputChange('role', value)}
                  error={errors?.role}
                  placeholder="Sélectionner un rôle"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Annuler
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            iconName="Save"
            iconPosition="left"
            iconSize={16}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'En cours...' : (mode === 'create' ? 'Créer l\'utilisateur' : 'Sauvegarder')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserModal;